import React, {useRef} from 'react';

import {ReactComponent as InvoiceIcon} from 'src/assets/icons/credit-card-02.svg';
import {UserProfileInterface} from 'src/reducers/users';
import {toast, ToastContainer} from 'react-toastify';
import {useNavigate} from 'react-router-dom';
import {SpinnerIcon} from 'src/components/icons';
import {BaseScreen, ScreenHeader} from '../Screen';
import {nFetch} from 'src/api/nFetch';
import {DataLoader} from 'src/components/DataLoader';
import {UserLoader} from 'src/components/UserLoader';
import {Invoice, InvoiceDetail} from 'src/api/types/billing';
import {loadScript} from 'src/lib/load-script';
import {ScreenNames} from '../../../lib/analytics/events';
import {InvoicePaymentHandler} from './InvoicePaymentHandler';

const ButtonStyle = {
    default: 'w-full px-4 py-2 rounded-lg bg-blue-500 text-white font-semibold text-xl',
    success:
        'rounded-[8px] bg-green-500 text-white w-14 md:w-full md:px-4 md:py-2 md:rounded-lg md:bg-green-500 md:text-white md:font-semibold md:text-xl',
    error: 'rounded-[8px] bg-red-500 text-white w-[75px] md:w-full md:px-4 md:py-2 md:rounded-lg md:bg-red-500 md:text-white md:font-semibold md:text-xl',
    disabled: 'w-full px-4 py-2 rounded-lg bg-gray-200 text-white font-semibold text-xl',
};

function Button(props: {
    text?: React.ReactNode;
    onClick?: () => void;
    iconLeft?: React.ReactNode;
    iconRight?: React.ReactNode;
    disabled?: boolean;
    style?: string;
}) {
    const style = props.style ?? ButtonStyle.default;
    return (
        <button className={props.disabled ? ButtonStyle.disabled : style} onClick={props.onClick}>
            <div className={'flex flex-row items-center justify-center'}>
                {props.iconLeft ? props.iconLeft : props.text ? <div className={'h-5 w-5'} /> : <></>}
                {props.text ? (
                    <span className={'font-inter mx-1 text-base'}>{props.text ?? ''}</span>
                ) : props.iconLeft && props.iconRight ? (
                    <span className={'font-inter mx-1 text-base'}></span>
                ) : (
                    <></>
                )}
                {props.iconRight ? props.iconRight : props.text ? <div className={'h-5 w-5'} /> : <></>}
            </div>
        </button>
    );
}

function ListInvoiceHandler(props: {
    invoice: Invoice;
    onNext: (invoice: Invoice) => void;
    onLoading: (loading: boolean) => void;
    onProcessing: (processing: boolean) => void;
}) {
    return new InvoicePaymentHandler({
        invoice: props.invoice,
        onNext: (invoice) => {
            if (invoice.status === 'PAID') {
                toast.success('Payment successful!');
                props.onNext(invoice);
            } else if (invoice.status === 'CANCELLED') {
                toast.error(`Payment cancelled. ${InvoicePaymentHandler.REFUND_MSG}`);
                props.onNext(invoice);
            } else {
                toast.error(`Payment failed. ${InvoicePaymentHandler.REFUND_MSG}`);
                props.onNext(invoice);
            }
        },
        onError: (error) => {
            toast.error(error.message);
        },
        onLoading: (loading) => {
            props.onLoading(loading);
        },
        onProcessing: (processing) => {
            props.onProcessing(processing);
        },
        cancelInvoice: false,
    });
}

function RzpPayButtonV2(props: {
    invoice: Invoice;
    scriptReady: boolean;
    initProcessingCallback: (processing: boolean) => void;
}) {
    const [loading, setLoading] = React.useState(false);
    const handler = useRef<InvoicePaymentHandler | undefined>(undefined);
    const [invoice, setInvoice] = React.useState<Invoice>(props.invoice);

    React.useEffect(() => {
        if (handler.current) {
            return;
        }
        handler.current = ListInvoiceHandler({
            invoice: invoice,
            onNext: (invoice) => {
                setInvoice(invoice);
            },
            onLoading: (loading) => {
                setLoading(loading);
            },
            onProcessing: (processing) => {
                if (props.invoice.status === 'PROCESSING') {
                    props.initProcessingCallback(processing);
                }
            },
        });
    });

    return (
        <>
            <Button
                text={
                    invoice.status === 'INIT'
                        ? 'Pay'
                        : invoice.status === 'PROCESSING'
                          ? ''
                          : invoice.status === 'PAID'
                            ? 'Paid'
                            : invoice.status === 'FINAL'
                              ? 'Paid'
                              : invoice.status === 'CANCELLED'
                                ? 'Cancelled'
                                : '???'
                }
                style={
                    invoice.status === 'INIT'
                        ? ButtonStyle.default
                        : invoice.status === 'PROCESSING'
                          ? ButtonStyle.disabled
                          : invoice.status === 'PAID'
                            ? ButtonStyle.success
                            : invoice.status === 'FINAL'
                              ? ButtonStyle.success
                              : invoice.status === 'CANCELLED'
                                ? ButtonStyle.error
                                : ButtonStyle.default
                }
                onClick={() => {
                    if (handler.current) {
                        if (handler.current?.hasNext()) {
                            handler.current?.next();
                        }
                    }
                }}
                iconLeft={(loading || invoice.status === 'PROCESSING') && <SpinnerIcon />}
                disabled={loading}
            />
        </>
    );
}

const InvoiceRowStyle = {
    header: 'px-2 flex flex-col w-[385px] md:w-full bg-gray-50 border border-solid border-transparent',
    default: 'px-2 flex flex-col bg-white w-[385px] md:w-full   border border-solid border-gray-200 border-b-0',
    y0: 'px-2 flex flex-col bg-white   border border-solid border-gray-200 border-y-0',
    t0: 'px-2 flex flex-col bg-white   border border-solid border-gray-200 border-t-0',
    last: 'px-2 flex flex-col bg-white w-[385px] md:w-full   border border-solid border-gray-200 rounded-b-lg',
};

const InvoiceCellStyle = {
    header: 'p-1 font-[400] text-[12px] md:w-1/6 md:p-3 md:font-semibold md:text-gray-600 md:text-[1rem]',
    default: 'p-1 font-[400] text-[12px] md:w-1/6 md:p-3 md:text-gray-600 md:text-[1rem]',
    compact: 'w-1/6 p-1',
};

function isoDate(date: string): string {
    return new Date(date).toISOString().split('T')[0];
}

function isoDateMview(dateString: string) {
    const date = new Date(dateString);
    const year = date.getFullYear().toString().slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return year + '/' + month + '/' + day;
}

function InvoiceRow(props: {invoice: Invoice; rzpScriptReady: boolean; last: boolean}) {
    const [processing, setProcessing] = React.useState(false);

    const invoiceParts = props.invoice.id.split('-');
    const shortInvoiceId = invoiceParts[invoiceParts.length - 1];

    return (
        <div className={props.last ? InvoiceRowStyle.last : InvoiceRowStyle.default}>
            <div className={'flex flex-row items-center'}>
                <div className={`${InvoiceCellStyle.default} w-[50px] overflow-x-auto md:w-full`}>{shortInvoiceId}</div>
                <div className={`${InvoiceCellStyle.default} hidden md:flex`}>
                    {isoDate(props.invoice.period_start)}
                </div>
                <div className={`${InvoiceCellStyle.default} flex w-[70px] items-center justify-center md:hidden`}>
                    {isoDateMview(props.invoice.period_start)}
                </div>
                <div className={`${InvoiceCellStyle.default} hidden md:flex`}>{isoDate(props.invoice.period_end)}</div>
                <div className={`${InvoiceCellStyle.default} flex w-[60px] items-center justify-center md:hidden`}>
                    {isoDateMview(props.invoice.period_end)}
                </div>
                <div className={`${InvoiceCellStyle.default} flex w-[40px] pl-4 md:w-full`}>
                    ${props.invoice.amount}
                </div>
                <div className={`${InvoiceCellStyle.default} hidden md:flex`}>{isoDate(props.invoice.date)}</div>
                <div className={`${InvoiceCellStyle.default} flex w-[70px] items-center justify-center md:hidden`}>
                    {isoDateMview(props.invoice.date)}
                </div>
                <div className={InvoiceCellStyle.compact}>
                    <RzpPayButtonV2
                        invoice={props.invoice}
                        scriptReady={props.rzpScriptReady}
                        initProcessingCallback={(processing) => {
                            setProcessing(processing);
                        }}
                    />
                </div>
            </div>
            {processing && (
                <div className={'flex flex-row items-center justify-center'}>
                    <span className={'text-sm italic text-gray-700'}>
                        It may take upto 20 minutes for the payment to reflect. Please wait...
                    </span>
                </div>
            )}
        </div>
    );
}

function InvoiceList(props: {user: UserProfileInterface; invoiceDetail?: InvoiceDetail; invoices: Invoice[]}) {
    const [rzpScriptReady, setRzpScriptReady] = React.useState(false);
    const mut = useRef(false);

    const navigate = useNavigate();
    const {invoices} = props;

    React.useEffect(() => {
        if (mut.current) {
            return;
        }
        mut.current = true;
        loadScript('https://checkout.razorpay.com/v1/checkout.js')
            .then((loaded) => {
                setRzpScriptReady(loaded);
            })
            .catch((err) => {
                console.error('failed to load razorpay script', err);
                setRzpScriptReady(false);
            });
    });

    if (invoices.length === 0) {
        return (
            <div className={InvoiceRowStyle.last}>
                <div className={'p-2'}>
                    <Button
                        text={'Select Plan'}
                        onClick={() => {
                            navigate('/settings/plans');
                        }}
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={InvoiceRowStyle.header}>
                <div className={'flex flex-row items-center overflow-x-auto md:w-auto md:overflow-x-hidden'}>
                    <div className={InvoiceCellStyle.header}>Invoice ID</div>
                    <div className={InvoiceCellStyle.header}>Start Date</div>
                    <div className={InvoiceCellStyle.header}>End Date</div>
                    <div className={InvoiceCellStyle.header}>Amount</div>
                    <div className={InvoiceCellStyle.header}>Due Date</div>
                    <div className={InvoiceCellStyle.header}>Payment</div>
                </div>
            </div>
            {invoices.map((invoice, idx) => (
                <>
                    <InvoiceRow
                        key={invoice.id}
                        invoice={invoice}
                        rzpScriptReady={rzpScriptReady}
                        last={idx === invoices.length - 1}
                    />
                </>
            ))}
        </>
    );
}

function InvoiceDetailsForm(props: {user: UserProfileInterface; invoiceDetail?: InvoiceDetail}) {
    const {invoiceDetail} = props;

    function BillingButton() {
        const navigate = useNavigate();

        return (
            <Button
                text={'Add Billing Information'}
                onClick={() => {
                    navigate('/settings/billing');
                }}
            />
        );
    }

    return (
        <>
            <ScreenHeader
                icon={<InvoiceIcon className={'h-6 w-6 stroke-black'} />}
                title={'Invoices'}
                className={'w-[385px] rounded-t-lg border border-solid border-gray-200 md:w-full'}
            />
            {
                <DataLoader
                    loader={async () => {
                        const org_uid = props.user.currentOrganization.uid;
                        const result = await nFetch<Invoice[]>(`/billing/invoices_by_org/${org_uid}`);
                        if (!!result.error) throw result.error;
                        return result.data ?? [];
                    }}>
                    {(data) => (
                        <>
                            {invoiceDetail === undefined && (
                                <div className={data.length > 0 ? InvoiceRowStyle.t0 : InvoiceRowStyle.y0}>
                                    <div className={'p-2'}>
                                        <BillingButton />
                                    </div>
                                </div>
                            )}
                            <InvoiceList user={props.user} invoiceDetail={invoiceDetail} invoices={data} />
                        </>
                    )}
                </DataLoader>
            }
        </>
    );
}

export default function InvoiceDetails() {
    const {INVOICE_SCREEN} = ScreenNames;

    return (
        <BaseScreen screenName={INVOICE_SCREEN} border={false}>
            <UserLoader>
                {(user) => (
                    <DataLoader
                        loader={async () => {
                            const org_uid = user.currentOrganization.uid;
                            const result = await nFetch<InvoiceDetail>(`/billing/detail/${org_uid}`);
                            if (!!result.error) throw result.error;
                            return result.data;
                        }}>
                        {(data) => <InvoiceDetailsForm user={user} invoiceDetail={data} />}
                    </DataLoader>
                )}
            </UserLoader>
            <ToastContainer position={'bottom-right'} />
        </BaseScreen>
    );
}
